import axios from "axios";

const PLATFORM_HOSTS = new Set(["rpsignage.com", "www.rpsignage.com", "rpsignage.in", "www.rpsignage.in", "localhost", "127.0.0.1"]);

function isLocalNetworkHost(host) {
  return (
    /^192\.168\.\d{1,3}\.\d{1,3}$/.test(host) ||
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host) ||
    /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(host)
  );
}

export function isCustomPublicHost(hostname) {
  const host = String(hostname || "").toLowerCase();
  return host && !PLATFORM_HOSTS.has(host) && !isLocalNetworkHost(host);
}

export function publicApiBase(resolvedHost) {
  const host = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  if (!resolvedHost && process.env.NODE_ENV === "development" && isLocalNetworkHost(host) && !["localhost", "127.0.0.1"].includes(host)) {
    return `${window.location.origin}/api/public`;
  }
  if (resolvedHost || isCustomPublicHost(typeof window !== "undefined" ? window.location.hostname : "")) {
    return `${window.location.origin}/api/public`;
  }
  return `${(process.env.REACT_APP_BACKEND_URL || "https://rpsignage.com").replace(/\/$/, "")}/api/public`;
}

export function customerTokenKey(clientRef) {
  return `shopper_token_${clientRef}`;
}

export function customerRefreshTokenKey(clientRef) {
  // Same key format ShopperAuthContext/storeApi.js uses for the "store"
  // scope - this context covers the "providers" (booking) scope, and both
  // can point at the same account, so keeping the key format identical
  // means whichever logs in most recently keeps both usable.
  return `shopper_refresh_${clientRef}`;
}

let publicCustomerRefreshInFlight = null;

export function createPublicCustomerApi(clientRef, resolvedHost, scope = "providers") {
  const base = `${publicApiBase(resolvedHost)}/${scope}/${encodeURIComponent(clientRef)}`;
  const authRoot = publicApiBase(resolvedHost).replace(/\/public$/, "");
  const tokenKey = customerTokenKey(clientRef);
  const refreshTokenKey = customerRefreshTokenKey(clientRef);
  const instance = axios.create({
    baseURL: base,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
  });
  instance.interceptors.request.use((cfg) => {
    const t = localStorage.getItem(tokenKey);
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
    return cfg;
  });
  // Same silent-refresh-and-retry pattern as storeApi.js / lib/api.js - this
  // context previously had no recovery from a 401 at all, so a customer
  // mid-booking got hard-logged-out after the access token's 12h expiry.
  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error?.response?.status;
      const cfg = error?.config || {};
      const rt = localStorage.getItem(refreshTokenKey);
      if (status !== 401 || cfg._retried || !rt) return Promise.reject(error);
      cfg._retried = true;
      try {
        if (!publicCustomerRefreshInFlight) {
          publicCustomerRefreshInFlight = axios
            .post(`${authRoot}/auth/refresh`, { refresh_token: rt }, { withCredentials: true })
            .finally(() => { publicCustomerRefreshInFlight = null; });
        }
        const { data } = await publicCustomerRefreshInFlight;
        if (!data?.access_token) throw new Error("refresh returned no access token");
        localStorage.setItem(tokenKey, data.access_token);
        if (data.refresh_token) localStorage.setItem(refreshTokenKey, data.refresh_token);
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${data.access_token}`;
        return instance(cfg);
      } catch {
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(refreshTokenKey);
        return Promise.reject(error);
      }
    }
  );
  return instance;
}

function legacyBookingPersistKey(clientId, branchId = "") {
  return `signage_active_booking_${clientId}_${branchId || "main"}`;
}

export function bookingPersistKey(clientId, branchId = "") {
  return `signage_active_bookings_${clientId}_${branchId || "main"}`;
}

export function isActiveBookingStatus(status) {
  return !["done", "cancelled"].includes(String(status || "").toLowerCase());
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function sortBookings(list) {
  return [...list].sort((a, b) => {
    const tokenDelta = Number(a.token || 0) - Number(b.token || 0);
    if (tokenDelta !== 0) return tokenDelta;
    return String(a.created_at || "").localeCompare(String(b.created_at || ""));
  });
}

function normalizeBookingEntry(entry) {
  if (!entry || typeof entry !== "object" || !entry.id) return null;
  return entry;
}

export function loadActiveBookings(clientId, branchId) {
  if (!clientId) return [];
  const today = todayIso();
  const key = bookingPersistKey(clientId, branchId);
  let list = [];

  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      list = Array.isArray(parsed) ? parsed : [parsed];
    }
  } catch {}

  if (!list.length) {
    try {
      const legacyRaw = localStorage.getItem(legacyBookingPersistKey(clientId, branchId));
      if (legacyRaw) {
        const legacy = JSON.parse(legacyRaw);
        if (legacy && typeof legacy === "object") list = [legacy];
      }
    } catch {}
  }

  const active = sortBookings(
    list
      .map(normalizeBookingEntry)
      .filter(Boolean)
      .filter((item) => item.date === today && isActiveBookingStatus(item.status))
  );

  try {
    if (active.length) {
      localStorage.setItem(key, JSON.stringify(active));
    } else {
      localStorage.removeItem(key);
    }
    localStorage.removeItem(legacyBookingPersistKey(clientId, branchId));
  } catch {}

  return active;
}

export function saveActiveBooking(clientId, branchId, booking) {
  if (!clientId || !booking?.id) return;
  const list = loadActiveBookings(clientId, branchId).filter((item) => item.id !== booking.id);
  if (isActiveBookingStatus(booking.status) && (booking.date || todayIso()) === todayIso()) {
    list.push({ ...booking, date: booking.date || todayIso() });
  }
  localStorage.setItem(bookingPersistKey(clientId, branchId), JSON.stringify(sortBookings(list)));
  localStorage.removeItem(legacyBookingPersistKey(clientId, branchId));
}

export function removeActiveBooking(clientId, branchId, bookingId) {
  if (!clientId || !bookingId) return;
  const list = loadActiveBookings(clientId, branchId).filter((item) => item.id !== bookingId);
  const key = bookingPersistKey(clientId, branchId);
  if (list.length) {
    localStorage.setItem(key, JSON.stringify(list));
  } else {
    localStorage.removeItem(key);
  }
}

export function loadActiveBooking(clientId, branchId) {
  const list = loadActiveBookings(clientId, branchId);
  return list[0] || null;
}

export function clearActiveBooking(clientId, branchId) {
  localStorage.removeItem(bookingPersistKey(clientId, branchId));
  localStorage.removeItem(legacyBookingPersistKey(clientId, branchId));
}
